import React, { useEffect, useRef, useState } from 'react';
import Modal from '@tencent/dui/lib/components/Modal';
import i18n from '@i18next/i18n';
import PageRange from '../range';
import { transaction } from '@src/js/utils';
import { NumberInput } from '@tencent/wb-ext-form';
import Checkbox from '@tencent/dui/lib/components/Checkbox';
import { convertCmToPx } from '@src/slide/infrastructure/module/independent/unit';
import style from './style.less';
import { DialogProps, QUICK_TOOL_AID_FEATURE, QUICK_TOOL_AID_POSITION } from '..';
import { showVipLinktext } from '@src/js/common/ark-extensions/dc-components/vip-linktext';
import { ISlideModel } from '@src/slide/domain/ui-model/element-modeles/Interface/slide-model.interface';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import { IBaseModel } from '@src/slide/domain/ui-model/element-modeles/Interface/base/base-model.interface';
import { IElementBaseModel } from '@src/slide/domain/ui-model/element-modeles/Interface/base/element-base-model.interface';
import { TaskPriority } from '@src/slide/infrastructure/module/dependent/taskManager';
import Loading from '@tencent/dui/lib/components/Loading';
import { isUiModelIsAttachment } from '@src/slide/domain/ui-model/elements/element-judge';
import { isLateXModel } from '@src/slide/application/service/addon/utils';
import type { IFrame } from '@src/slide/domain/ui-model/element-modeles/property/frame/frame.interface';

const NUMBER_RANGE = {
    MAX: 50,
    MIN: 0,
    STEP: 0.1,
};

const apprec = 'batsetpicsize';

type IPictureSizeDialog = React.FC<DialogProps>;
const PictureSizeDialog: IPictureSizeDialog = (props: DialogProps) => {
    const {
        visible, onClose, onOk, position, services,
    } = props;
    const [width, setWidth] = useState(5);
    const [height, setHeight] = useState(5);
    const [useHeight, setUseHeight] = useState(true);
    const [useWidth, setUseWidth] = useState(false);
    const [lockAspectRatio, setLockAspectRatio] = useState(true);
    const [slideList, setSlideList] = useState<ISlideModel[]>();

    const isPicture = (model: IElementBaseModel) => {
        if (model.type === UIModelType.IMAGE && !isUiModelIsAttachment(model) && !isLateXModel(model)) {
            return true;
        }
        return false;
    };

    const updatePictureSize = (
        model: IBaseModel, updateModelArr: IElementBaseModel[],
        updateFrameArr: IFrame[],
    ) => {
        if (model.type === UIModelType.GROUP) {
            model.children.forEach((child) => {
                updatePictureSize(child, updateModelArr, updateFrameArr);
            });
        }
        if (isPicture(model as IElementBaseModel)) {
            const modelFrame = (model as IElementBaseModel).getFrame?.().getFrameInfo();
            if (modelFrame) {
                const frame = {
                    ...modelFrame,
                    width: convertCmToPx(width),
                    height: convertCmToPx(height),
                };
                updateModelArr.push(model as IElementBaseModel);
                updateFrameArr.push(frame);
            }
        }
    };

    const updatePictureWidth = (
        model: IBaseModel, updateModelArr: IElementBaseModel[],
        updateValueArr: number[],
    ) => {
        if (model.type === UIModelType.GROUP) {
            model.children.forEach((child) => {
                updatePictureWidth(child, updateModelArr, updateValueArr);
            });
        }
        if (isPicture(model as IElementBaseModel)) {
            updateModelArr.push(model as IElementBaseModel);
            updateValueArr.push(convertCmToPx(width));
        }
    };

    const updatePictureHeight = (
        model: IBaseModel, updateModelArr: IElementBaseModel[],
        updateValueArr: number[],
    ) => {
        if (model.type === UIModelType.GROUP) {
            model.children.forEach((child) => {
                updatePictureHeight(child, updateModelArr, updateValueArr);
            });
        }
        if (isPicture(model as IElementBaseModel)) {
            updateModelArr.push(model as IElementBaseModel);
            updateValueArr.push(convertCmToPx(height));
        }
    };

    const handleOk = async (event) => {
        // 根据 dui 文档，没有 event 参数表示是 enter 按键触发。
        // 按键触发保存时由于不会触发 input 的 blur，也就无法触发 input 值的合法化校验，导致非法参数被保存，所以在这里禁用 enter 保存
        if (!event) {
            return;
        }
        if (!onOk(apprec, i18n.t('批量设置图片尺寸')) || !slideList) {
            return;
        }
        const loading = Loading.show({ tip: i18n.t('加载中...') });
        const timeout = setTimeout(async () => {
            await Promise.all(slideList?.map(async (slide) => {
                !slide.childrenConverted && await slide.startConvertChildrenTask(TaskPriority.HIGH);
            }));
            transaction(updatePicture);
            onClose();
            loading.close();
            clearTimeout(timeout);
        }, 0);
    };

    const updatePicture = () => {
        const updateModelArr: IElementBaseModel[] = [];
        const updateValueArr: number[] = [];
        const updateFrameArr: IFrame[] = [];
        const slide = slideList?.[0];
        if (!lockAspectRatio) {
            // 不锁定纵横比，同时调整宽高
            slideList?.forEach((slide) => {
                slide.children.forEach((child) => {
                    updatePictureSize(child, updateModelArr, updateFrameArr);
                });
            });
            slide?.getBatchUpdateFrame().batchUpdateFrame({ frame: updateFrameArr, updateModelArr });
        } else if (useWidth) {
            slideList?.forEach((slide) => {
                slide.children.forEach((child) => {
                    updatePictureWidth(child, updateModelArr, updateValueArr);
                });
            });
            slide?.getBatchUpdateFrame().batchUpdateWidth({ value: updateValueArr, updateModelArr });
        } else {
            slideList?.forEach((slide) => {
                slide.children.forEach((child) => {
                    updatePictureHeight(child, updateModelArr, updateValueArr);
                });
            });
            slide?.getBatchUpdateFrame().batchUpdateHeight({ value: updateValueArr, updateModelArr });
        }
    };

    const getNumberInString = (value: string): number => {
        const regex = /-?\d+(?:\.\d+)?/g;
        const matches = value.match(regex);
        return Number(Number(matches?.[0]).toFixed(2)) || 0;
    };

    const getWidthOrHeightValue = (value: number, use: boolean): string => {
        if (lockAspectRatio) {
            return use ? value.toFixed(2).toString() : '';
        }
        return value.toFixed(2).toString();
    };

    const vipLinkRef = useRef(null);
    useEffect(() => {
        showVipLinktext({
            ref: vipLinkRef.current,
            name: i18n.t('批量设置图片尺寸'),
            feature: QUICK_TOOL_AID_FEATURE,
            position,
            apprec,
            anchor: 'PPT-TOOLS',
        });
    }, [vipLinkRef.current]);

    return (
        <Modal
            title={i18n.t('批量设置图片尺寸')}
            visible={visible}
            onClose={onClose}
            onCancel={onClose}
            onOk={handleOk}
            className={'muti-pic-size-dialog'}
        >
            <div style={{ width: '100%', marginBottom: '10px' }} ref={vipLinkRef}></div>
            <div className={style['size-container']}>
                <div className={style['single-container']}>
                    <NumberInput
                        min={NUMBER_RANGE.MIN}
                        max={NUMBER_RANGE.MAX}
                        allowDecimal={true}
                        toFixed={2}
                        value={getWidthOrHeightValue(height, useHeight)}
                        step={NUMBER_RANGE.STEP}
                        suffix={i18n.t('厘米')}
                        style={{ width: '168px' }}
                        onChange={(value) => {
                            setUseHeight(true);
                            setUseWidth(false);
                            setHeight(Number(getNumberInString(value)));
                        }}>
                    </NumberInput>
                    <div className={style.text}>{i18n.t('高度')}</div>
                </div>
                <div className={style['single-container']}>
                    <NumberInput
                        min={NUMBER_RANGE.MIN}
                        max={NUMBER_RANGE.MAX}
                        allowDecimal={true}
                        toFixed={2}
                        value={getWidthOrHeightValue(width, useWidth)}
                        suffix={i18n.t('厘米')}
                        step={NUMBER_RANGE.STEP}
                        style={{ width: '168px' }}
                        onChange={(value) => {
                            setUseWidth(true);
                            setUseHeight(false);
                            setWidth(Number(getNumberInString(value)));
                        }}>
                    </NumberInput>
                    <div className={style.text}>{i18n.t('宽度')}</div>
                </div>
            </div>
            <div style={{ marginBottom: '20px' }}>
                <Checkbox
                    checked={lockAspectRatio}
                    style={{ fontSize: '12px', color: 'rgba(0, 0, 0, 0.64)' }}
                    onChange={checked => setLockAspectRatio(checked)}>
                    {i18n.t('锁定纵横比')}
                </Checkbox>
            </div>
            <PageRange
                needGetSelectModels={true}
                onCheckStateChange={list => setSlideList(list)}
                services={{
                    masterLayoutEditService: services.masterLayoutEditService,
                    selectionService: services.selectionService,
                }}>
            </PageRange>
        </Modal>
    );
};

export default PictureSizeDialog;
